import * as B from '@blueprintjs/core';
import {DateRange} from '@blueprintjs/datetime2';
import React from 'react';

import {CategoryAlertProperties, CompositeAlertProperties} from 'app/modules/Remote/Feature/types';
import {ThresholdAlertProperties} from 'app/modules/Remote/Feature/types';
import * as C from 'app/utils/constants';
import * as conversionUtils from 'app/utils/conversionUtils';
import * as layerUtils from 'app/utils/layerUtils';
import {UNITS_UI} from 'app/utils/mathUtils';

import {formatCompositeExpression, formatOverlaySelections, overlayLabel} from './alertPolicyUtils';
import {AlertFormState} from './useAlertForm';

export const PolicySummary: React.FC<{
  formState: AlertFormState;
  projectOverlays: Array<{id: number; name: string}>;
  organizationOverlays: Array<{id: number; name: string}>;
  displayAreaUnit: string;
  previousIds: number[];
  currentIds: number[];
}> = React.memo((props) => {
  const {
    formState,
    projectOverlays,
    organizationOverlays,
    displayAreaUnit,
    previousIds,
    currentIds,
  } = props;
  const getOverlayLabel = React.useCallback(
    (id: number) => overlayLabel(id, projectOverlays, organizationOverlays),
    [projectOverlays, organizationOverlays]
  );

  const isThreshold = formState.alertType === C.CUSTOM_ALERT_TYPE_THRESHOLD;
  const isCategory = formState.alertType === C.CUSTOM_ALERT_TYPE_CATEGORY;
  const isVegetationDrop = formState.alertType === C.CUSTOM_ALERT_TYPE_VEGETATION_DROP;
  const compositeProps =
    formState.alertType === C.CUSTOM_ALERT_TYPE_COMPOSITE
      ? (formState.properties as unknown as CompositeAlertProperties)
      : undefined;
  const thresholdProps = isThreshold
    ? (formState.properties as unknown as ThresholdAlertProperties)
    : undefined;
  const categoryProps = isCategory
    ? (formState.properties as unknown as CategoryAlertProperties)
    : undefined;
  const percentProps = thresholdProps ?? compositeProps;
  const includeOverlays = formState.properties.includeOverlays ?? [];
  const excludeOverlays = formState.properties.excludeOverlays ?? [];

  const formatDateRange = React.useMemo(() => {
    const dateRange = formState.properties.dateRange as DateRange | undefined;
    if (!dateRange || !dateRange[0] || !dateRange[1]) return null;
    const formatDate = (date: Date) =>
      date.toLocaleDateString('en-US', {
        year: undefined,
        month: 'long',
        day: 'numeric',
      });
    return `${formatDate(dateRange[0])} - ${formatDate(dateRange[1])}`;
  }, [formState.properties.dateRange]);

  const formatMinimumArea = React.useMemo(() => {
    const valueM2 = formState.properties.minimumAlertingArea ?? 0;
    if (valueM2 === 0) return null;
    const displayValue = conversionUtils.convert(valueM2, C.UNIT_AREA_M2, displayAreaUnit);
    if (!Number.isFinite(displayValue)) return null;
    const rounded = Math.max(0, Math.round(displayValue * 100) / 100);
    return `${rounded} ${UNITS_UI[displayAreaUnit]}`;
  }, [formState.properties.minimumAlertingArea, displayAreaUnit]);

  const minimumSizeLines = React.useMemo(() => {
    const lines: string[] = [];
    if (formatMinimumArea) lines.push(formatMinimumArea);
    const percent = percentProps?.percentOfProperty ?? 0;
    if (percent > 0) lines.push(`${percent}% of property area`);
    return lines.length ? lines : ['Any'];
  }, [formatMinimumArea, percentProps?.percentOfProperty]);

  const diffText = React.useMemo(() => {
    const prevOnly = previousIds.filter((id) => !currentIds.includes(id));
    const currOnly = currentIds.filter((id) => !previousIds.includes(id));
    if (prevOnly.length === 0 && currOnly.length === 0)
      return 'No new properties will be enrolled.';
    const parts: string[] = [];
    if (currOnly.length)
      parts.push(
        `${currOnly.length} ${currOnly.length === 1 ? 'property' : 'properties'} will be enrolled`
      );
    if (prevOnly.length)
      parts.push(
        `${prevOnly.length} ${prevOnly.length === 1 ? 'property' : 'properties'} will be unenrolled`
      );
    return parts.join(' and ') + '.';
  }, [previousIds, currentIds]);

  return (
    <B.Callout intent={B.Intent.PRIMARY} icon={null}>
      <div>
        <div>
          {isVegetationDrop ? (
            <strong>Unseasonal vegetation drop</strong>
          ) : (
            <>
              <strong>Dataset:</strong> {formState.layer.display}
            </>
          )}
        </div>
        {isThreshold && thresholdProps && (
          <>
            <div>
              <strong>Threshold:</strong> {thresholdProps.direction} {thresholdProps.value}
            </div>
            <div>
              <strong>Number of consecutive scenes:</strong> {thresholdProps.numConsecutiveScenes}
            </div>
          </>
        )}
        {compositeProps && (
          <>
            <div>
              <strong>Fires when:</strong>{' '}
              {formatCompositeExpression(
                layerUtils.parseLayerKey(formState.layer.key).sourceId,
                compositeProps
              )}
            </div>
            <div>
              <strong>Number of consecutive scenes:</strong> {compositeProps.numConsecutiveScenes}
            </div>
          </>
        )}
        {isCategory &&
          categoryProps &&
          'layerLegendMap' in formState.layer &&
          formState.layer.layerLegendMap && (
            <div>
              <strong>Category change:</strong> From{' '}
              {categoryProps.fromCategories
                .map((catId) => formState.layer.layerLegendMap?.[catId]?.label || catId)
                .join(', ')}{' '}
              to{' '}
              {categoryProps.toCategories
                .map((catId) => formState.layer.layerLegendMap?.[catId]?.label || catId)
                .join(', ')}
            </div>
          )}
        {includeOverlays.length > 0 && (
          <div>
            <strong>Only inside:</strong>{' '}
            {formatOverlaySelections(includeOverlays, getOverlayLabel)}
          </div>
        )}
        {excludeOverlays.length > 0 && (
          <div>
            <strong>Never inside:</strong>{' '}
            {formatOverlaySelections(excludeOverlays, getOverlayLabel)}
          </div>
        )}
        {minimumSizeLines.map((line) => (
          <div key={line}>
            <strong>Minimum size of change:</strong> {line}
          </div>
        ))}
        {formatDateRange && (
          <div>
            <strong>Annual alerting window:</strong> {formatDateRange}
          </div>
        )}
      </div>
      <div style={{marginTop: '1rem'}}>{diffText}</div>
    </B.Callout>
  );
});
